/**
 * MOTOR DE VARIAÇÕES
 * Em vez de desenhar 300 telas na mão, cada nicho gera dezenas de versões
 * COERENTES combinando: paleta × tipografia × forma × modo (claro/escuro).
 * Toda variação sai com contraste calculado — nunca uma combinação ilegível.
 */
import { lum, mix, tintaSobre } from './tema';

/* ---------------- ingredientes ---------------- */

export const PALETAS = [
  { id: 'azul',      nome: 'Azul confiança',   cor: '#1a73e8', apoio: '#0b57d0' },
  { id: 'verde',     nome: 'Verde natureza',   cor: '#2f7d4f', apoio: '#12a46b' },
  { id: 'dourado',   nome: 'Dourado premium',  cor: '#c9a24b', apoio: '#8a6d1f' },
  { id: 'terracota', nome: 'Terracota quente', cor: '#b4552f', apoio: '#8a3f20' },
  { id: 'rosa',      nome: 'Rosa moderno',     cor: '#e0729b', apoio: '#b6486f' },
  { id: 'roxo',      nome: 'Roxo criativo',    cor: '#7c4dff', apoio: '#5a2fd0' },
  { id: 'laranja',   nome: 'Laranja energia',  cor: '#f5852b', apoio: '#c2621a' },
  { id: 'vermelho',  nome: 'Vermelho urgência',cor: '#d93025', apoio: '#a81c14' },
  { id: 'turquesa',  nome: 'Turquesa fresco',  cor: '#12b39a', apoio: '#0b8a77' },
  { id: 'grafite',   nome: 'Grafite neutro',   cor: '#3c4043', apoio: '#202124' },
];

export const TIPOGRAFIAS = [
  { id: 'moderna',  nome: 'Moderna',   corpo: 'Inter,sans-serif',                titulo: 'Inter,sans-serif',                     peso: 800 },
  { id: 'elegante', nome: 'Elegante',  corpo: 'Inter,sans-serif',                titulo: "'Playfair Display',Georgia,serif",     peso: 700 },
  { id: 'amigavel', nome: 'Amigável',  corpo: "'Outfit',Inter,sans-serif",       titulo: "'Outfit',Inter,sans-serif",            peso: 800 },
  { id: 'tecnica',  nome: 'Técnica',   corpo: "'Space Grotesk',Inter,sans-serif",titulo: "'Space Grotesk',Inter,sans-serif",     peso: 700 },
  { id: 'impacto',  nome: 'Impacto',   corpo: 'Inter,sans-serif',                titulo: "'Bebas Neue',Inter,sans-serif",        peso: 400, tituloGrande: true },
  { id: 'editorial',nome: 'Editorial', corpo: 'Inter,sans-serif',                titulo: "'Fraunces',Georgia,serif",             peso: 700 },
];

export const FORMAS = [
  { id: 'suave',   nome: 'Suave',   radius: '16px', avatarRadius: '50%',  botao: '16px' },
  { id: 'pilula',  nome: 'Pílula',  radius: '999px', avatarRadius: '50%', botao: '999px' },
  { id: 'reta',    nome: 'Reta',    radius: '8px',  avatarRadius: '14px', botao: '8px' },
];

export const MODOS = [
  { id: 'claro',  nome: 'Claro' },
  { id: 'escuro', nome: 'Escuro' },
];

/** Combinações que fazem sentido por nicho (curadoria, não força bruta). */
const POR_NICHO = {
  seguranca:   { paletas: ['azul', 'grafite', 'vermelho', 'turquesa'],  tipos: ['moderna', 'tecnica', 'impacto'] },
  saude:       { paletas: ['azul', 'turquesa', 'verde'],                tipos: ['moderna', 'amigavel', 'elegante'] },
  beleza:      { paletas: ['rosa', 'roxo', 'dourado'],                  tipos: ['amigavel', 'elegante', 'editorial'] },
  food:        { paletas: ['terracota', 'vermelho', 'laranja', 'dourado'], tipos: ['editorial', 'amigavel', 'moderna'] },
  automotivo:  { paletas: ['laranja', 'vermelho', 'grafite', 'azul'],   tipos: ['impacto', 'tecnica', 'moderna'] },
  construcao:  { paletas: ['laranja', 'grafite', 'azul'],               tipos: ['impacto', 'moderna', 'tecnica'] },
  pet:         { paletas: ['verde', 'turquesa', 'laranja', 'rosa'],     tipos: ['amigavel', 'moderna'] },
  tech:        { paletas: ['azul', 'roxo', 'turquesa', 'grafite'],      tipos: ['tecnica', 'moderna'] },
  juridico:    { paletas: ['grafite', 'dourado', 'azul'],               tipos: ['elegante', 'editorial', 'moderna'] },
  moda:        { paletas: ['grafite', 'rosa', 'dourado', 'roxo'],       tipos: ['editorial', 'elegante', 'amigavel'] },
  urgencia:    { paletas: ['vermelho', 'laranja', 'azul'],              tipos: ['impacto', 'moderna'] },
  universal:   { paletas: PALETAS.map(p => p.id),                       tipos: ['moderna', 'amigavel'] },
};

export const NICHOS = [
  { id: 'seguranca', nome: 'Segurança eletrônica' },
  { id: 'saude', nome: 'Saúde e clínicas' },
  { id: 'beleza', nome: 'Beleza e estética' },
  { id: 'food', nome: 'Alimentação' },
  { id: 'automotivo', nome: 'Automotivo' },
  { id: 'construcao', nome: 'Construção e reformas' },
  { id: 'pet', nome: 'Pet e veterinária' },
  { id: 'tech', nome: 'Tecnologia' },
  { id: 'juridico', nome: 'Jurídico e contábil' },
  { id: 'moda', nome: 'Moda e varejo' },
  { id: 'urgencia', nome: 'Serviços 24h' },
  { id: 'universal', nome: 'Universal' },
];

/* ---------------- geração de um tema completo ---------------- */

function temaDe(paleta, tipo, forma, modo) {
  const cor = paleta.cor;
  const claro = modo.id === 'claro';

  // fundos derivados da marca (bem sutis) para o site não ficar genérico
  const bg = claro ? mix(cor, '#ffffff', 0.965) : mix(cor, '#0b0d12', 0.88);
  const card = claro ? '#ffffff' : mix(cor, '#141821', 0.86);
  const borda = claro ? mix(cor, '#e6e8ec', 0.86) : mix(cor, '#2a2f3a', 0.8);
  const ink = claro ? mix(cor, '#16181d', 0.88) : '#f2f4f8';
  const sub = claro ? mix(cor, '#5b6270', 0.8) : mix('#ffffff', bg, 0.32);

  // acento precisa ser legível sobre o fundo; se não for, escurece/clareia
  let acento = cor;
  const contrasteOk = Math.abs(lum(acento) - lum(bg)) > 0.28;
  if (!contrasteOk) acento = claro ? mix(cor, '#000000', 0.35) : mix(cor, '#ffffff', 0.35);

  return {
    bg, ink, sub,
    accent: acento, onAccent: tintaSobre(acento),
    accent2: paleta.apoio,
    card, cardBorder: `1px solid ${borda}`,
    chip: claro ? mix(cor, '#f4f6f8', 0.9) : card,
    chipBorder: `1px solid ${borda}`,
    chipInk: claro ? mix(cor, '#3c4043', 0.7) : '#dfe4ee',
    avatarBg: claro ? mix(cor, '#ffffff', 0.86) : mix(cor, '#1b2029', 0.7),
    avatarInk: acento,
    dock: claro ? 'rgba(255,255,255,.95)' : 'rgba(12,15,20,.94)',
    dockBorder: `1px solid ${borda}`,
    star: '#FBBC04',
    font: tipo.corpo, headFont: tipo.titulo,
    pesoTitulo: tipo.peso, tituloGrande: !!tipo.tituloGrande,
    radius: forma.radius, avatarRadius: forma.avatarRadius,
    sombraBtn: forma.id === 'suave',
  };
}

/**
 * Gera as variações de um nicho.
 * Ex.: seguranca -> 4 paletas × 3 tipografias × 3 formas × 2 modos = 72 versões.
 */
export function gerarVariacoes(nichoId = 'universal', limite = 36) {
  const regra = POR_NICHO[nichoId] || POR_NICHO.universal;
  const paletas = PALETAS.filter(p => regra.paletas.includes(p.id));
  const tipos = TIPOGRAFIAS.filter(t => regra.tipos.includes(t.id));
  const out = [];

  for (const modo of MODOS) {
    for (const p of paletas) {
      for (const t of tipos) {
        for (const f of FORMAS) {
          out.push({
            id: `${nichoId}-${p.id}-${t.id}-${f.id}-${modo.id}`,
            nome: `${p.nome} · ${t.nome} · ${f.nome}`,
            modo: modo.nome,
            paleta: p.id, tipografia: t.id, forma: f.id,
            t: temaDe(p, t, f, modo),
          });
        }
      }
    }
  }
  // intercala claro/escuro para a galeria não ficar toda clara e depois toda escura
  const claros = out.filter(v => v.modo === 'Claro');
  const escuros = out.filter(v => v.modo === 'Escuro');
  const alternado = [];
  for (let i = 0; i < Math.max(claros.length, escuros.length); i++) {
    if (claros[i]) alternado.push(claros[i]);
    if (escuros[i]) alternado.push(escuros[i]);
  }
  return alternado.slice(0, limite);
}

/** Quantas versões cada nicho consegue gerar (para mostrar na interface). */
export function totalVariacoes(nichoId) {
  const r = POR_NICHO[nichoId] || POR_NICHO.universal;
  return r.paletas.length * r.tipos.length * FORMAS.length * MODOS.length;
}
